'use client';

import Image from 'next/image';
import { useEffect, useState, type ElementType } from 'react';
import { BellRing, CalendarClock, CalendarSync, MessageCircle } from 'lucide-react';
import { Icon } from '@/components/ui/Icon';
import { cn } from '@/lib/utils';

const STEP_INTERVAL_MS = 5000;

interface FlowStep {
  icon: ElementType;
  image: string;
  label: string;
  text: string;
  title: string;
}

const flowSteps: FlowStep[] = [
  {
    icon: MessageCircle,
    image: '/images/mockups/como_funciona_1.png',
    label: '01',
    title: 'Tus clientes escriben por WhatsApp',
    text: 'El cliente pregunta por una cita en el número que conectaste mediante WhatsApp Business. OficioYa responde y ofrece disponibilidad real.',
  },
  {
    icon: CalendarClock,
    image: '/images/mockups/como_funciona_2.png',
    label: '02',
    title: 'Recibes notificación de nueva cita',
    text: 'Cuando la cita queda agendada, tu te enteras al instante. Tú ves quién viene, qué servicio pidió y a qué hora.',
  },
  {
    icon: CalendarSync,
    image: '/images/mockups/como_funciona_4.png',
    label: '03',
    title: 'La cita se consulta, cambia o cancela',
    text: 'El cliente puede consultar su próxima cita, pedir un cambio o cancelarla desde la conversación.',
  },
  {
    icon: BellRing,
    image: '/images/mockups/como_funciona_3.png',
    label: '04',
    title: 'Tú decides cuándo enviar un recordatorio',
    text: 'Desde la agenda eliges una cita y envías un recordatorio por WhatsApp al cliente cuando lo necesites.',
  },
];

export function HowItWorks() {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeStep = flowSteps[activeIndex];

  useEffect(() => {
    flowSteps.forEach((step) => {
      const preloadImage = new window.Image();
      preloadImage.src = step.image;
    });
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % flowSteps.length);
    }, STEP_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [activeIndex]);

  return (
    <section id="como-funciona" data-section="como_funciona" className="relative overflow-hidden border-y border-border bg-bg-secondary py-20 sm:py-24">
      <div className="container mx-auto px-6">
        <div className="mx-auto mb-12 max-w-4xl text-center">
          {/* <Badge variant="outline" className="mb-5">
            Así trabaja tu agenda por WhatsApp
          </Badge> */}
          <h2 className="font-heading text-3xl font-bold leading-tight text-text-primary sm:text-5xl">
            Así trabaja tu asistente
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-base leading-8 text-text-secondary">
            Tus clientes escriben al número que conectas mediante WhatsApp Business. <br />
            OficioYa pone detrás una <u>agenda que responde, avisa y mantiene cada cita bajo control.</u>
          </p>
        </div>
        <div className="grid gap-8 lg:grid-cols-[0.86fr_1.14fr] lg:items-stretch">
          <div className="rounded-mockup border border-border bg-bg-primary/72 p-3 shadow-[0_18px_50px_-36px_rgb(0_0_0/0.85)]">
            <div className="grid gap-3" role="tablist" aria-label="Pasos de agendamiento por WhatsApp">
              {flowSteps.map((step, index) => {
                const isActive = activeIndex === index;

                return (
                  <button
                    key={step.title}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls="how-it-works-panel"
                    onClick={() => setActiveIndex(index)}
                    className={cn(
                      'group grid min-h-[5.5rem] grid-cols-[auto_1fr_auto] items-center gap-4 rounded-card border px-4 py-3 text-left transition-colors',
                      isActive
                        ? 'border-accent/45 bg-accent/10 text-text-primary'
                        : 'border-border bg-bg-secondary/55 text-text-secondary hover:border-accent/35 hover:bg-bg-secondary'
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-11 w-11 items-center justify-center rounded-button border transition-colors',
                        isActive
                          ? 'border-accent/40 bg-accent/15 text-accent'
                          : 'border-border-strong bg-bg-primary/70 text-text-secondary group-hover:text-accent'
                      )}
                    >
                      <Icon icon={step.icon} size={21} />
                    </span>
                    <span className="font-heading text-lg font-semibold leading-tight text-text-primary">
                      {step.title}
                    </span>
                    <span className="font-heading text-sm font-semibold text-accent">{step.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div
            id="how-it-works-panel"
            role="tabpanel"
            className="overflow-hidden rounded-mockup border border-accent/25 bg-bg-primary shadow-mockup"
          >
            <div className="border-b border-border p-5 sm:p-6">
              <h3 className="font-heading text-2xl font-semibold leading-tight text-text-primary sm:text-3xl">
                {activeStep.title}
              </h3>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-text-secondary sm:text-base">
                {activeStep.text}
              </p>
            </div>

            <div className="relative min-h-[22rem] bg-[radial-gradient(circle_at_top,rgb(var(--color-accent)/0.12),transparent_46%)] p-2 sm:min-h-[31rem] lg:min-h-[34rem]">
              <div className="absolute m-3 inline-flex gap-2 items-center rounded-chip border border-accent/30 bg-accent/10 px-3 py-1.5 text-xs font-semibold uppercase text-accent">
                <Icon icon={activeStep.icon} size={15} />
                Paso {activeStep.label}
              </div>
              <Image
                src={activeStep.image}
                alt={activeStep.title}
                fill
                sizes="(min-width: 1024px) 55vw, 100vw"
                className="object-contain p-1 sm:p-2"
                priority={activeIndex === 0}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
